// useAuthStore3.tsx
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { jwtDecode } from "jwt-decode";
//import axios from '../api/axiosSetup3';

const API_BASE = '/api/identity' // import.meta.env.VITE_AUTHURL;

export type FsmState = 
  | 'AUTHORIZED' 
  | 'LOGIN' 
  | 'FORGOT_PASSWORD'
  | 'FORGOT_PASSWORD_SUCCESS'
  | 'WAIT_FOR_EMAIL_CONFIRMATION' 
  | 'NOT_REGISTERED' 
  | 'REGISTERED'
  | 'ONBOARDING'
  | 'REGISTERED_AND_SET_UP'
;


interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  isHydrated: boolean; // Флаг: прочитали ли мы данные из localStorage
  error: string | null;
  customerId: number | null;
  subtenantId: number | null;
  isEmailConfirmed: boolean;
  isConfirmed: boolean;
  userId: number | null;
  appMode: string | null;
  email: string | null;
  userName: string | null;
  appRole: string | null;
  lang: string | null;
  authState: FsmState;
  setAuthState: (state: FsmState) => void;
  setCustomerId: (newCustomerId: number) => void;
  login: (email: string, password: string) => Promise<any>;
  loginViaGoogle: () => Promise<string>;
  signup: (data: any) => Promise<any>;
  register: (data: any) => Promise<any>;
  registerViaGoogle: () => Promise<any>;
  next: (data: any) => Promise<any>;
  resendEmail: (email: string, ) => Promise<boolean>;
  logout: () => void;
  refresh: () => Promise<void>;
  resetPassword: (email: string) => Promise<any>;
  updateCustomer: (customerId: number) => Promise<any>;
  hasRole: (role: string) => boolean;
  setHydrated: () => void;
  stopUserService: () => Promise<any>;
  handleGoogleCallback: (code: string) => Promise<any>;
  sendResetPasswordEmail: (email: string) => Promise<any>;
  sendInvitationEmail: (subtenant_id: number, email: string) => Promise<void>;
  isSubtenantActive: (subtenant_id: number) => Promise<boolean>;
  getUserList: (subtenant_id: number) => Promise<any>;
  checkEmail: (email: string) => Promise<any>;
}

async function apiRequest(
  endpoint: string,
  body: object,
  token?: string | null,
  onLoadingChange?: (loading: boolean) => void,
  onError?: (msg: string) => void,
): Promise<any> {
  onLoadingChange?.(true);
  try {
    const doFetch = (t?: string | null) =>
      fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(t ? { "Authorization": `Bearer ${t}` } : {}),
        },
        body: JSON.stringify(body),
      });    

    let response = await doFetch(token);
    // if (response.status === 403) {
    //   const errorData = await response.json().catch(() => ({}));
    //   const msg: string = errorData.message || errorData.detail || "";
    //   if (msg.toLowerCase().includes("expired")) {
    //     await useAuthStore.getState().refresh();
    //     const newToken = useAuthStore.getState().accessToken;
    //     response = await doFetch(newToken);
    //   }
    // }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.detail || errorData.username || "Request failed");
    }

    return await response.json();
  } catch (err: any) {
    onError?.(err.message);
    throw err;
  } finally {
    onLoadingChange?.(false);
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      isLoading: false,
      isHydrated: false, // Изначально false
      error: null,
      customerId: null,
      subtenantId: null,
      isEmailConfirmed: false,
      isConfirmed: false,
      userId: null,
      appMode: null,
      email: null,
      userName: null,
      appRole: null,
      lang: null,

      // authMode: 'login', // Начальное значение
      authState: 'LOGIN',
      // setAuthMode: (mode) => set({ authMode: mode }),
      setAuthState: (state: FsmState) => set({ authState: state }),
      setCustomerId: (newCustomerId) => set({ customerId: newCustomerId }),

      setTokens: (tokens: any) => {
        console.log('[AuthStore] setTokens called', tokens, new Error().stack);
        set({ ...tokens });
      },
      
      clearAuth: () => {
        console.log('[AuthStore] clearAuth called', new Error().stack);
        set({ accessToken: null, userName: null, subtenantId: null, customerId: null });
      },

      setHydrated: () => set({ isHydrated: true }),

      login: async (email: string, password: string) => {
        set({ error: null });
        const data = await apiRequest(
          "/login/",
          { username: email, password },
          null,
          (loading) => set({ isLoading: loading }),
          (msg) => set({ error: msg }),
        );

        const decoded: any = jwtDecode(data.access);

        set({
          accessToken: data.access,
          refreshToken: data.refresh,
          customerId: decoded.customer_id,
          subtenantId: decoded.subtenant_id,
          isEmailConfirmed: decoded.is_email_confirmed,
          isConfirmed: decoded.is_confirmed,
          userId: decoded.user_id,
          appMode: decoded.app_mode,
          email: decoded.email,
          userName: decoded.username,
          appRole: decoded.app_role,
          lang: decoded.lang || 'uk',
        });

        return data;
      },

      loginViaGoogle: async (): Promise<string> => {
        set({ error: null });

        const res = await fetch(`${API_BASE}/auth/google/urls/`, { method: "GET", });  
        const urls = await res.json();
        let url = urls.login_url;
        console.log('url',url);

        if (!url || typeof url !== 'string') {
          console.error('google url not found');
          return '';
        }

        if (window.location.hostname === 'localhost') {
          const parsedUrl = new URL(url);
          // Указываем адрес нашего ЛОКАЛЬНОГО фронтенда, где сидит компонент-колбэк
          parsedUrl.searchParams.set('redirect_uri', 'http://localhost:5173/auth/google/callback'); 
          url = parsedUrl.toString();
        }

        return url;
      },

      signup: async (data) => {
        set({ error: null });
        try {
          return await apiRequest("/registration/lpr/", data, null, (l) => set({ isLoading: l }), );
        //return await apiRequest("/registration/lpr/", data, null, (l) => set({ isLoading: l }), (e) => set({ error: e }));
        } catch(e: any) {
          return {ok: false, message: e.message}
        }
      },

      register: async (data: any) => {
        set({ error: null });
        try {          
          return await apiRequest("/registration/lpr/", data, null, (l) => set({ isLoading: l }), );
        } catch(e: any) {
          return {ok: false, message: e.message}
        }
      },
      
      registerViaGoogle: async (): Promise<string> => {
        set({ error: null });

        const res = await fetch(`${API_BASE}/auth/google/urls/`, { method: "GET", });  
        const urls = await res.json();
        const url = urls.login_url;
        console.log('url',url);

        if (!url || typeof url !== 'string') {
          console.error('google url not found');
          return '';
        }
        return url;
      },

      next: async (data) => {
        let message = '';
        await apiRequest(
          "/registration_next/", 
          data, 
          get().accessToken, 
          (l) => set({ isLoading: l }), 
          (msg) => set({ error: msg }),);
      },

      resendEmail: async (email: string,) => {
        set({ error: null });
        const data = await apiRequest('/registration/resend-email/', {email}, null,
          (loading) => set({ isLoading: loading }),
          (msg) => set({ error: msg }),
        );
        return data.detail == 'ok';
      },

      logout: () => {
        console.log('[logout] called, clearing tokens');
        set({ error: null });
        set({
          accessToken: null,
          refreshToken: null,
          customerId: null,
          subtenantId: null,
          isEmailConfirmed: false,
          isConfirmed: false,
          userId: null,
          appMode: null,
          email: null,
          userName: null,
          appRole: null,
          lang: null,
          authState: 'LOGIN',
        });
        console.log('[logout] after set, accessToken:', useAuthStore.getState().accessToken);
        localStorage.removeItem('auth-storage'); // Очищаем хранилище
      },

      refresh: async () => {
        set({ error: null });
        try {
          const data = await apiRequest(
            "/token/refresh/",
            { refresh: get().refreshToken },
            null,
          );
          set({ accessToken: data.access });
          set({ customerId: data.user.customer_id });
          set({ subtenantId: data.user.subtenant_id });
          set({ isConfirmed: data.user.is_confirmed });
          set({ appRole: data.user.app_role });

        } catch (e) {
          get().logout(); // Если рефреш не удался — выкидываем
        }
      },

      resetPassword: async (email: string) => {
        set({ error: null });
        return await apiRequest("/auth/password/reset/", { email }, null, (l) => set({ isLoading: l }), (e) => set({ error: e }));
      },

      updateCustomer: async (customerId: number) => {
        return await apiRequest(
          "/dj_user/",
          { customer_id: customerId },
          get().accessToken,
          (l) => set({ isLoading: l }),
          (e) => set({ error: e }),
        );
      },

      hasRole: (role) => {
        const roles = get().appRole?.split(",") || [];
        return roles.includes(role);
      },
      
      stopUserService: async () => {
        await apiRequest("rpc/sf_stop_service/", {}, get().accessToken, (l) => set({ isLoading: l }), (e) => set({ error: e }));
        get().logout();
        get().setAuthState('LOGIN');
      },

      handleGoogleCallback: async (code: string) => {
        set({ error: null });
        
        // Отправляем POST на ваш эндпоинт dj-rest-auth / Allauth
        const data = await apiRequest(
          "/auth/google/lpr/",
          { code: code },
          null,
          (loading) => set({ isLoading: loading }),
          (msg) => set({ error: msg }),
        );

        // Декодируем ваш кастомный токен, полученный от Django
        const decoded: any = jwtDecode(data.access);

        const oldAuthState = get().authState;
        // Логика FSM: 
        // если пользователь был NOT_REGISTERED, то после успешного коллбэка он становится REGISTERED, 
        // иначе — AUTHORIZED
        const newAuthState = oldAuthState === 'NOT_REGISTERED' ? 'REGISTERED' : 'AUTHORIZED';
        set({
          accessToken: data.access,
          refreshToken: data.refresh,
          customerId: decoded.customer_id,
          subtenantId: decoded.subtenant_id,
          isEmailConfirmed: decoded.is_email_confirmed,
          isConfirmed: decoded.is_confirmed,
          userId: decoded.user_id,
          appMode: decoded.app_mode,
          email: decoded.email,
          userName: decoded.username,
          appRole: decoded.app_role,
          lang: decoded.lang || 'uk',
          authState: newAuthState, 
        });

        return data;
      },

      sendResetPasswordEmail: async (email: string) => {
        set({ error: null });
        return await apiRequest("/auth/password/reset/", { email }, null, (l) => set({ isLoading: l }), (e) => set({ error: e }));
      },

      sendInvitationEmail: async (subtenant_id: number, email: string) => {
        await apiRequest('/invite/', {
          subtenant_id,
          email,
        },
        get().accessToken,
        (l) => set({ isLoading: l }),
        (e) => set({ error: e }),
        );
      },

      isSubtenantActive: async (subtenant_id: number): Promise<boolean> => {
        try {
          const res = await fetch(`${API_BASE}/userlist/?subtenant_id=${subtenant_id}`, {
             method: "GET", 
             headers: { 
              "Content-Type": "application/json",
              "Authorization": `Bearer ${get().accessToken}` 
            } 
          });

          if (!res.ok) {
            console.error('Failed to fetch subtenant users:', res.statusText);
            return false;
          }
          const data = await res.json();
          return data.users.length > 0;
        } catch (e) {
          console.error('Failed to check if subtenant is active:', e);
          return false;
        }
      },

      getUserList: async (param: any) => {
        try {
          const url = !param.subtenantId 
            ? `${API_BASE}/userlist/` 
            : `${API_BASE}/userlist/?subtenant_id=${param.subtenantId}`;
            // : `${API_BASE}/userlist/?has_subtenant=false`;
          const res = await fetch(url, {
             method: "GET", 
             headers: { 
              "Content-Type": "application/json",
              "Authorization": `Bearer ${get().accessToken}` 
            } 
          });
          const data = await res.json();
          return data.users;
        } catch (e) {
          console.error('Failed to fetch subtenant list:', e);
          return [];
        }
      },

      checkEmail: async (email: string): Promise<boolean> => {
        try {
          const data = await apiRequest('/check_email/', { email, }, get().accessToken);
          // {"exists":true,"status":{"verified":false,"primary":true,"user_id":95}}
          return !data.exists; // Если email существует — он недоступен для регистрации
        } catch (e) {
          console.error('Failed to check email:', e);
          return false;
        }
      },

    }),
    {
      name: 'auth-storage', // Ключ в LocalStorage
      storage: createJSONStorage(() => localStorage),
      // ОЧЕНЬ ВАЖНО: говорим Zustand, когда данные загружены из памяти в стор
      onRehydrateStorage: () => (state, error) => {
        
        console.log('[AuthStore] rehydrated state:', {
          accessToken: state?.accessToken,
          userId: state?.userId,
          timestamp: new Date().toISOString(),
        });

        if (error) {
            console.error('Ошибка гидратации:', error);
        }
        console.log('Гидратация завершена успешно');
        state?.setHydrated(); // Вызываем экшен из стора
    },
    }
  )
);